import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';
import {
  ShipmentOrderLegRunForPlanEntity,
  ShipmentOrderLegRunListResponse,
} from 'src/entities/ShipmentOrderLegRun.entity';

import { SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY } from './useQueryShipmentOrderLegRuns';

/**
 * 1 リクエストで引く件数。id を URL に並べるので、長くなりすぎないよう区切る
 * （API の上限は 2000 件だが、そこまで並べると URL が数十 KB になる）
 */
export const SUPPLEMENTAL_LEG_RUN_IDS_PER_REQUEST = 200;

const EMPTY: ShipmentOrderLegRunForPlanEntity[] = [];

/**
 * deliveries に出てくるのに未割当一覧に無い案件の id（昇順・重複なし）。
 *
 * 一覧は積地の時刻で引くので、前日に積んで表示日には降ろすだけの案件（宵積み）が
 * 入らない。deliveries の legRun は compact で数量などが抜けているため、この id を
 * useQuerySupplementalLegRuns で引いて補う (log_web_issues#343)。
 */
export function findLegRunIdsToSupplement(
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
  legRuns: readonly ShipmentOrderLegRunForPlanEntity[],
): number[] {
  const known = new Set(legRuns.map((lr) => lr.id));
  const missing = new Set<number>();
  deliveries.forEach((delivery) => delivery.shipmentOrderLegRuns.forEach((lr) => {
    if (lr.id != null && !known.has(lr.id)) missing.add(lr.id);
  }));
  return [...missing].sort((a, b) => a - b);
}

const fetchLegRunsByIds = async (ids: readonly number[]): Promise<ShipmentOrderLegRunListResponse[]> => {
  const chunks: number[][] = [];
  for (let index = 0; index < ids.length; index += SUPPLEMENTAL_LEG_RUN_IDS_PER_REQUEST) {
    chunks.push(ids.slice(index, index + SUPPLEMENTAL_LEG_RUN_IDS_PER_REQUEST));
  }
  const responses = await Promise.all(chunks.map((chunk) => {
    const query = chunk.map((id) => `ids[]=${id}`).join('&');
    return axios.get<ShipmentOrderLegRunListResponse, AxiosResponse<ShipmentOrderLegRunListResponse>>(
      `/api/v5/shipment_order_leg_runs?${query}&page=1&per_page=${SUPPLEMENTAL_LEG_RUN_IDS_PER_REQUEST}`,
    );
  }));
  return responses.map((response) => response.data);
};

/**
 * 指定した id の案件（非 compact）。deliveries の legRun を補う材料にだけ使い、
 * 未割当リストには混ぜない（前日の案件が表示日の未割当に出てしまう）。
 *
 * キーを未割当一覧と同じ接頭辞にし、形も InfiniteData に揃える。案件の編集や配車の変更で
 * 掛かる `invalidateQueries({ queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY] })` に乗せて
 * 一緒に取り直させるため。`setQueriesData` で `pages` を書き換える既存処理もそのまま通る。
 */
export const useQuerySupplementalLegRuns = (ids: readonly number[]): ShipmentOrderLegRunForPlanEntity[] => {
  const query = useQuery({
    queryKey: [SHIPMENT_ORDER_LEG_RUNS_QUERY_KEY, 'supplemental', ids.join(',')],
    queryFn: async () => {
      const pages = await fetchLegRunsByIds(ids);
      return { pages, pageParams: pages.map((_, index) => index + 1) };
    },
    enabled: ids.length > 0,
    staleTime: Infinity,
    // 配車を変えると対象の id が変わってキーも変わる。取り直しの間に数量が 0 に戻って
    // 見えないよう、前の結果を出したままにする（余分な案件が混ざっても補完には影響しない）
    placeholderData: keepPreviousData,
  });

  const { data } = query;
  return useMemo(
    () => (ids.length === 0 || !data ? EMPTY : data.pages.flatMap((page) => page.data)),
    [ids.length, data],
  );
};
